
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldAlert, CheckCircle2, AlertTriangle, Info, X, ScrollText } from 'lucide-react';
import { cn } from '../lib/utils';

interface TradingDisclaimerModalProps {
  isOpen: boolean;
  onAgree: (data: { timestamp: string; ip_address: string; disclaimer_version: string }) => void;
}

export default function TradingDisclaimerModal({ isOpen, onAgree }: TradingDisclaimerModalProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // If we're within 20px of the bottom, consider it scrolled
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleAgree = () => {
    if (isChecked && hasScrolledToBottom) {
      onAgree({
        timestamp: new Date().toISOString(),
        ip_address: '127.0.0.1', // Mock IP for now
        disclaimer_version: '1.0.0'
      });
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setIsChecked(false);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
          {/* Backdrop with blur */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            data-testid="disclaimer-modal"
            className="relative w-full max-w-3xl bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="px-8 py-6 border-b border-[var(--card-header-border)] bg-[var(--card-header-bg)] flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-600 shadow-inner">
                <ShieldAlert size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-black text-[var(--app-text)] uppercase italic tracking-tight">Trading Risk Disclosure</h2>
                <p className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest mt-0.5">Mandatory Institutional Acknowledgment</p>
              </div>
            </div>

            {/* Scrollable Content */}
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-8 space-y-6 scrollbar-thin scrollbar-thumb-slate-200"
            >
              <div className="space-y-6 text-base text-[var(--app-text)] leading-relaxed">
                <section className="space-y-3">
                  <h3 className="text-sm font-black uppercase italic text-rose-600 flex items-center gap-2">
                    <AlertTriangle size={16} /> 1. High Risk Warning
                  </h3>
                  <p className="font-medium">
                    Trading stocks, options, futures, and cryptocurrencies involves substantial risk of loss and is not suitable for every investor. The valuation of financial instruments may fluctuate, and as a result, clients may lose more than their original investment.
                  </p>
                </section>

                <section className="space-y-3">
                  <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
                    <DollarSign size={16} className="text-emerald-600" /> 2. Loss of Capital
                  </h3>
                  <p className="text-[var(--muted-text)]">
                    All trading strategies are used at your own risk. You should only trade with capital that you can afford to lose. Past performance is not indicative of future results. Adversyn Trading does not guarantee any specific outcome or profit.
                  </p>
                </section>

                <section className="space-y-3">
                  <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
                    <Info size={16} className="text-blue-600" /> 3. No Financial Advice
                  </h3>
                  <p className="text-[var(--muted-text)]">
                    The content provided through Adversyn Trading, including AI-generated analysis, council debates, and strategy suggestions, is for informational purposes only. It does not constitute financial, investment, legal, or tax advice. You should consult with a qualified professional before making any financial decisions.
                  </p>
                </section>

                <section className="space-y-3">
                  <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
                    <Brain size={16} className="text-purple-600" /> 4. AI Analysis Limitations
                  </h3>
                  <p className="text-[var(--muted-text)]">
                    Our AI Council and analysis tools use complex algorithms to process market data. However, AI can make mistakes, hallucinate data, or fail to account for "black swan" events. Users must verify all AI-generated insights against primary data sources.
                  </p>
                </section>

                <section className="space-y-3">
                  <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
                    <RefreshCw size={16} className="text-amber-600" /> 5. Broker Execution Responsibility
                  </h3>
                  <p className="text-[var(--muted-text)]">
                    Adversyn Trading is a software interface and is not a broker-dealer. All trades are executed through your connected third-party brokerage account. We are not responsible for execution errors, slippage, system outages, or broker-side failures.
                  </p>
                </section>

                <div className="p-6 bg-slate-50 border border-slate-100 rounded-2xl mt-8">
                  <p className="text-xs text-slate-500 font-medium italic text-center">
                    By proceeding, you acknowledge that you have read this disclosure in its entirety and agree to the terms of use of the Adversyn Trading platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer / Controls */}
            <div className="p-8 border-t border-[var(--card-header-border)] bg-[var(--card-header-bg)] space-y-6">
              <div className="flex items-start gap-3">
                <div className="pt-0.5">
                  <button
                    data-testid="disclaimer-checkbox"
                    disabled={!hasScrolledToBottom}
                    onClick={() => setIsChecked(!isChecked)}
                    className={cn(
                      "w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                      isChecked ? "bg-emerald-600 border-emerald-600 text-white" : "border-[var(--card-border)] bg-[var(--input-bg)]",
                      !hasScrolledToBottom && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {isChecked && <CheckCircle2 size={14} />}
                  </button>
                </div>
                <div className="flex-1">
                  <label 
                    className={cn(
                      "text-base font-bold text-[var(--app-text)] cursor-pointer select-none",
                      !hasScrolledToBottom && "text-[var(--muted-text)] opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => hasScrolledToBottom && setIsChecked(!isChecked)}
                  >
                    I have read and understand the risks of trading.
                  </label>
                  {!hasScrolledToBottom && (
                    <p className="text-xs font-bold text-rose-500 uppercase tracking-widest mt-1">
                      Please scroll to the bottom to enable acknowledgment
                    </p>
                  )}
                </div>
              </div>

              <button
                data-testid="disclaimer-btn-agree"
                disabled={!isChecked || !hasScrolledToBottom}
                onClick={handleAgree}
                className={cn(
                  "w-full py-4 rounded-2xl text-base font-black uppercase italic transition-all shadow-xl",
                  isChecked && hasScrolledToBottom
                    ? "bg-emerald-600 text-white shadow-emerald-600/20 hover:bg-emerald-500"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                )}
              >
                Agree & Continue
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Add missing icons locally if needed or import them
const Brain = ({ size, className }: { size: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-1.54Z"/>
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-1.54Z"/>
  </svg>
);

const DollarSign = ({ size, className }: { size: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const RefreshCw = ({ size, className }: { size: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/>
  </svg>
);
